/**
 * Pure helpers for `/admin/ops/global-metrics` visualization.
 * All series are populated exclusively from API payloads — no synthetic streams.
 */

export const OPS_HIST_LEN = 96;

/**
 * Canonical spark keys — match `data-spark` on dashboard markup.
 * visibility: `admin` = business / Channel outcome safe; `sa` = infrastructure diagnostics (never tenant-admin).
 */
export const OPS_SPARK_SPECS = [
  { key: "fcmQ", label: "Alerts waiting", stroke: "#22c55e", visibility: "sa" },
  { key: "mqttD", label: "Device channel waiting", stroke: "#38bdf8", visibility: "sa" },
  { key: "evPct", label: "Live updates load %", stroke: "#a855f7", visibility: "sa" },
  { key: "fcmLag", label: "Alert delivery lag", stroke: "#f97316", visibility: "sa" },
  { key: "evDrop", label: "Live updates dropped", stroke: "#fb7185", visibility: "sa" },
  { key: "mqttDrop", label: "Device channel dropped", stroke: "#eab308", visibility: "sa" },
  { key: "fcmDrop", label: "Alert delivery failures", stroke: "#ef4444", visibility: "admin" },
  { key: "tgQ", label: "Telegram waiting", stroke: "#14b8a6", visibility: "sa" },
];

/** Ops Live sparks visible for this principal (defense in depth vs API scope). */
export function opsSparkSpecsForPrincipal(me) {
  const role = String(me && me.role ? me.role : "").trim();
  if (role === "superadmin" || role === "sa_staff") {
    return OPS_SPARK_SPECS.filter((s) => s.visibility === "sa" || s.visibility === "admin");
  }
  return OPS_SPARK_SPECS.filter((s) => s.visibility === "admin");
}

/** Query `scope=` for `/admin/ops/global-metrics` — `sa` if any spark needs SA payload. */
export function opsMetricsQueryScopeForSparkSpecs(specs) {
  const list = Array.isArray(specs) && specs.length ? specs : OPS_SPARK_SPECS;
  return list.some((s) => s.visibility === "sa") ? "sa" : "admin";
}

export function createOpsHist(specs) {
  const list = Array.isArray(specs) && specs.length ? specs : OPS_SPARK_SPECS;
  const o = {};
  for (const s of list) o[s.key] = [];
  return o;
}

/** True when API returned the v2 envelope `{ scope, allowed_specs, data }`. */
export function unwrapOpsMetricsEnvelope(raw) {
  if (!raw || typeof raw !== "object") return { data: raw || {}, scope: "sa", allowed_specs: null };
  if (raw.data != null && Array.isArray(raw.allowed_specs)) {
    const scope = String(raw.scope || "sa");
    const base =
      raw.data && typeof raw.data === "object" && !Array.isArray(raw.data) ? { ...raw.data } : {};
    base.metrics_scope = scope;
    return { data: base, scope, allowed_specs: raw.allowed_specs };
  }
  const scope = String(raw.metrics_scope || "sa");
  return { data: raw, scope, allowed_specs: null };
}

/** Build ordered spark specs from server `allowed_specs` (single source of truth). */
export function opsSparkSpecsFromAllowedKeys(allowedKeys) {
  const keys = Array.isArray(allowedKeys) ? allowedKeys : [];
  const byKey = new Map(OPS_SPARK_SPECS.map((s) => [s.key, s]));
  const out = [];
  for (const k of keys) {
    const spec = byKey.get(String(k || ""));
    if (spec) out.push(spec);
  }
  return out;
}

function _push(arr, v) {
  const n = Number(v);
  arr.push(Number.isFinite(n) ? n : 0);
  while (arr.length > OPS_HIST_LEN) arr.shift();
}

/**
 * Append one sample derived from a single `collect_global_metrics()` payload.
 */
export function pushOpsHistFromPayload(hist, data) {
  if (!hist || !data) return;
  const k = data.kpi || {};
  const ev = data.events || {};
  const tg = data.telegram || {};
  const cap = Number(ev.subscriber_cap) || 1;
  const sub = Number(ev.subscriber_count) || 0;
  const evPct = cap > 0 ? Math.min(100, (sub / cap) * 100) : 0;
  let tgQ = Number(tg.queue_size);
  if (!Number.isFinite(tgQ) || tgQ < 0) tgQ = 0;

  if (Object.prototype.hasOwnProperty.call(hist, "fcmQ")) {
    _push(hist.fcmQ, Number(k.fcm_queue_depth) || 0);
  }
  if (Object.prototype.hasOwnProperty.call(hist, "mqttD")) {
    _push(hist.mqttD, Number(k.mqtt_ingest_depth) || 0);
  }
  if (Object.prototype.hasOwnProperty.call(hist, "evPct")) {
    _push(hist.evPct, evPct);
  }
  if (Object.prototype.hasOwnProperty.call(hist, "fcmLag")) {
    _push(hist.fcmLag, Number(k.fcm_oldest_age_ms) || 0);
  }
  if (Object.prototype.hasOwnProperty.call(hist, "evDrop")) {
    _push(hist.evDrop, Number(ev.subscriber_dropped_total) || 0);
  }
  if (Object.prototype.hasOwnProperty.call(hist, "mqttDrop")) {
    _push(hist.mqttDrop, Number(k.mqtt_ingest_dropped) || 0);
  }
  if (Object.prototype.hasOwnProperty.call(hist, "fcmDrop")) {
    _push(hist.fcmDrop, Number(k.fcm_dropped_total) || 0);
  }
  if (Object.prototype.hasOwnProperty.call(hist, "tgQ")) {
    _push(hist.tgQ, tgQ);
  }
}

/** Latest sample formatted for the spark cell (stable text; updates each tick). */
/** @param {string} hex `#rrggbb` */
function hexToRgba(hex, a) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || ""));
  if (!m) return `rgba(148, 163, 184, ${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}, ${a})`;
}

export function formatSparkDisplayValue(key, v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  switch (key) {
    case "evPct":
      return `${n.toFixed(1)}%`;
    case "fcmLag":
      return `${Math.round(n)} ms`;
    default:
      return String(Math.round(n));
  }
}

/** True when the formatted metric reads as numeric zero (for emphasis styling). */
export function isOpsSparkNumericZero(key, v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return false;
  switch (key) {
    case "evPct":
      return Math.abs(n) < 1e-9;
    case "fcmLag":
      return Math.round(n) === 0;
    default:
      return Math.round(n) === 0;
  }
}

/**
 * @param {HTMLCanvasElement | null} canvas
 * @param {number[]} series
 * @param {string} stroke line colour
 * @param {number | null | undefined} hoverIndex — sample index in ``vals`` (same as scrub); draws guide + dot
 */
export function drawOpsSparkline(canvas, series, stroke, hoverIndex = null) {
  if (!canvas || !series || !series.length) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const r = window.devicePixelRatio || 1;
  const wCss = canvas.clientWidth || 280;
  const hCss = canvas.clientHeight || 72;
  if (canvas.width !== Math.floor(wCss * r) || canvas.height !== Math.floor(hCss * r)) {
    canvas.width = Math.floor(wCss * r);
    canvas.height = Math.floor(hCss * r);
  }
  ctx.setTransform(r, 0, 0, r, 0, 0);
  ctx.clearRect(0, 0, wCss, hCss);
  const pad = 6;
  const vals = series.slice(-OPS_HIST_LEN);
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  if (hi - lo < 1e-6) {
    lo -= 1;
    hi += 1;
  }
  const span = hi - lo;
  ctx.strokeStyle = "rgba(148,163,184,0.35)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad, hCss - pad);
  ctx.lineTo(wCss - pad, hCss - pad);
  ctx.stroke();
  const n = vals.length;

  const strokeCol = stroke || "#22c55e";

  const xyAt = (i) => {
    const ii = Math.max(0, Math.min(n - 1, i));
    const x =
      n <= 1 ? pad + (wCss - pad * 2) / 2 : pad + ((wCss - pad * 2) * ii) / Math.max(1, n - 1);
    const t = (vals[ii] - lo) / span;
    const y = pad + (1 - t) * (hCss - pad * 2);
    return { x, y };
  };

  if (n >= 2) {
    ctx.strokeStyle = strokeCol;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const { x, y } = xyAt(i);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  } else if (n === 1) {
    const { x, y } = xyAt(0);
    ctx.fillStyle = strokeCol;
    ctx.beginPath();
    ctx.arc(x, y, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  if (hoverIndex != null && Number.isFinite(Number(hoverIndex)) && n >= 1) {
    const idx = Math.max(0, Math.min(n - 1, Math.round(Number(hoverIndex))));
    const { x, y } = xyAt(idx);
    const accent = strokeCol || "#94a3b8";
    ctx.save();
    ctx.lineCap = "round";
    ctx.strokeStyle = hexToRgba(accent, 0.2);
    ctx.lineWidth = 5;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(x, pad);
    ctx.lineTo(x, hCss - pad);
    ctx.stroke();
    ctx.strokeStyle = hexToRgba(accent, 0.72);
    ctx.lineWidth = 1.35;
    ctx.setLineDash([5, 6]);
    ctx.beginPath();
    ctx.moveTo(x, pad);
    ctx.lineTo(x, hCss - pad);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = hexToRgba(accent, 0.42);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = "rgba(255,255,255,0.92)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, 5.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = strokeCol;
    ctx.beginPath();
    ctx.arc(x, y, 3.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

/**
 * Redraw every canvas under `rootEl` that has a matching `[data-spark]` key in `hist`.
 */
export function redrawOpsSparkGrid(rootEl, hist, specs) {
  if (!rootEl || !hist) return;
  const list = Array.isArray(specs) && specs.length ? specs : OPS_SPARK_SPECS;
  const hover = rootEl._sparkHover;
  for (const spec of list) {
    const cell = rootEl.querySelector(`[data-spark="${spec.key}"]`);
    const c = cell && cell.querySelector("canvas");
    const valEl = cell && cell.querySelector(".ops-spark-value");
    const peakEl = cell && cell.querySelector(".ops-spark-peak");
    const series = hist[spec.key];
    const hi =
      hover && hover.key === spec.key && Number.isFinite(hover.idx) ? hover.idx : null;
    if (c && series) drawOpsSparkline(c, series, spec.stroke, hi);
    if (valEl) {
      const last = series && series.length ? series[series.length - 1] : NaN;
      const txt = formatSparkDisplayValue(spec.key, last);
      if (valEl.textContent !== txt) valEl.textContent = txt;
      valEl.classList.toggle("ops-num-zero", isOpsSparkNumericZero(spec.key, last));
    }
    if (peakEl) {
      const vals = series && series.length ? series.slice(-OPS_HIST_LEN) : [];
      const pkNum = vals.length ? Math.max(...vals) : NaN;
      const pk = vals.length ? formatSparkDisplayValue(spec.key, pkNum) : "—";
      if (peakEl.textContent !== pk) peakEl.textContent = pk;
      peakEl.classList.toggle("ops-num-zero", vals.length ? isOpsSparkNumericZero(spec.key, pkNum) : false);
    }
  }
}

/**
 * Hover over a spark canvas → floating tooltip with value at the nearest sample
 * (same indexing as the drawn polyline). Idempotent per grid root.
 * Call `rootEl._opsSparkHoverTeardown()` before removing the grid (see ops-live stopAll).
 *
 * @param {HTMLElement | null} rootEl — usually `#opsSparksGrid`
 * @param {() => Record<string, number[]>} getHist — returns live history object
 */
export function ensureOpsSparkHover(rootEl, getHist, sparkSpecs) {
  if (!rootEl) return;

  let tip = document.getElementById("opsSparkFloater");
  if (!tip) {
    tip = document.createElement("div");
    tip.className = "ops-spark-floater";
    tip.id = "opsSparkFloater";
    tip.setAttribute("role", "tooltip");
    tip.setAttribute("aria-hidden", "true");
    document.body.appendChild(tip);
  }

  /** Refresh getter every metrics tick without tearing down listeners (SSE ~1s). */
  rootEl._sparkHoverGetHist = getHist;
  if (rootEl.dataset.opsSparkHoverBound === "1") {
    return;
  }

  rootEl.dataset.opsSparkHoverBound = "1";

  const resolveHist = () =>
    typeof rootEl._sparkHoverGetHist === "function" ? rootEl._sparkHoverGetHist() : null;

  const pad = 6;

  const hide = () => {
    tip.classList.remove("ops-spark-floater--on");
    tip.setAttribute("aria-hidden", "true");
    tip.style.removeProperty("--ops-floater-accent");
  };

  let tipPosRaf = null;
  let lastTipEv = null;

  const positionTip = (ev) => {
    lastTipEv = ev;
    if (tipPosRaf != null) return;
    tipPosRaf = window.requestAnimationFrame(() => {
      tipPosRaf = null;
      const e = lastTipEv;
      if (!e || !tip.classList.contains("ops-spark-floater--on")) return;
      const tw = tip.offsetWidth || 200;
      const th = tip.offsetHeight || 72;
      let left = e.clientX + 16;
      let top = e.clientY + 16;
      if (left + tw > window.innerWidth - 12) left = e.clientX - tw - 16;
      if (top + th > window.innerHeight - 12) top = e.clientY - th - 16;
      tip.style.left = `${Math.max(10, left)}px`;
      tip.style.top = `${Math.max(10, top)}px`;
    });
  };

  let hoverDrawRaf = null;
  const queueHoverDraw = () => {
    if (hoverDrawRaf != null) return;
    hoverDrawRaf = window.requestAnimationFrame(() => {
      hoverDrawRaf = null;
      const h = resolveHist();
      if (h) redrawOpsSparkGrid(rootEl, h, sparkSpecs);
    });
  };

  rootEl._cancelSparkHoverRaf = () => {
    if (hoverDrawRaf != null) {
      cancelAnimationFrame(hoverDrawRaf);
      hoverDrawRaf = null;
    }
    if (tipPosRaf != null) {
      cancelAnimationFrame(tipPosRaf);
      tipPosRaf = null;
    }
  };

  const onMove = (ev) => {
    const hist = resolveHist();
    const cellEl = ev.target.closest(".ops-spark-cell");
    const canvas =
      cellEl && rootEl.contains(cellEl) ? cellEl.querySelector(".ops-spark-canvas") : null;
    if (!cellEl || !canvas) {
      rootEl._sparkHover = null;
      hide();
      if (hist) redrawOpsSparkGrid(rootEl, hist, sparkSpecs);
      return;
    }
    const cell = cellEl;
    const key = cell && cell.getAttribute("data-spark");
    const series = key && hist && hist[key];
    if (!series || !series.length) {
      rootEl._sparkHover = null;
      hide();
      if (hist) redrawOpsSparkGrid(rootEl, hist, sparkSpecs);
      return;
    }
    const vals = series.slice(-OPS_HIST_LEN);
    const n = vals.length;
    const rect = canvas.getBoundingClientRect();
    const wCss = rect.width;
    const x = ev.clientX - rect.left;
    const inner = Math.max(1e-6, wCss - pad * 2);
    const t = Math.max(0, Math.min(1, (x - pad) / inner));
    const idx = n <= 1 ? 0 : Math.round(t * (n - 1));
    const val = vals[idx];
    const spec = OPS_SPARK_SPECS.find((s) => s.key === key);
    const lab = spec ? spec.label : key;

    rootEl._sparkHover = { key, idx };
    queueHoverDraw();

    if (spec && spec.stroke) tip.style.setProperty("--ops-floater-accent", spec.stroke);
    else tip.style.removeProperty("--ops-floater-accent");

    tip.setAttribute("aria-hidden", "false");
    tip.classList.add("ops-spark-floater--on");
    tip.replaceChildren();
    const title = document.createElement("div");
    title.className = "ops-spark-floater__title";
    title.textContent = lab;
    const row = document.createElement("div");
    row.className =
      "ops-spark-floater__val mono" + (isOpsSparkNumericZero(key, val) ? " ops-num-zero" : "");
    row.textContent = formatSparkDisplayValue(key, val);
    const meta = document.createElement("div");
    meta.className = "ops-spark-floater__meta muted";
    meta.textContent = n > 0 ? `${idx + 1} / ${n}` : "";
    tip.appendChild(title);
    tip.appendChild(row);
    tip.appendChild(meta);

    positionTip(ev);
  };

  const onLeave = () => {
    rootEl._sparkHover = null;
    hide();
    const h = resolveHist();
    if (h) redrawOpsSparkGrid(rootEl, h, sparkSpecs);
  };

  rootEl.addEventListener("mousemove", onMove, true);
  rootEl.addEventListener("mouseleave", onLeave, true);

  rootEl._opsSparkHoverTeardown = () => {
    rootEl.removeEventListener("mousemove", onMove, true);
    rootEl.removeEventListener("mouseleave", onLeave, true);
    if (typeof rootEl._cancelSparkHoverRaf === "function") rootEl._cancelSparkHoverRaf();
    delete rootEl._cancelSparkHoverRaf;
    delete rootEl._opsSparkHoverTeardown;
    delete rootEl.dataset.opsSparkHoverBound;
    delete rootEl._sparkHoverGetHist;
    delete rootEl._sparkHover;
    hide();
  };
}

export function opsMetricsStale(data, intervalMs) {
  const ts = Number(data && data.ts) || 0;
  if (!ts) return true;
  const now = Math.floor(Date.now() / 1000);
  const drift = now - ts;
  const intervalSec = Math.max(1, Number(intervalMs) / 1000);
  return drift > Math.max(25, intervalSec * 5);
}
